import CloseIcon from '@mui/icons-material/Close';
import { Alert, Box, Fade, IconButton, Snackbar } from '@mui/material';
import React, { FC } from 'react';
import hotToast, { Toaster as HotToaster } from 'react-hot-toast';

export const Toaster = HotToaster;
export type SeverityType = 'success' | 'info' | 'warning' | 'error';

interface Props {
  message: string;
  severity?: SeverityType;
}

interface ToastProps extends Props {
  visible: boolean;
  onClose: (e: React.SyntheticEvent) => void;
}

const ALERT_SEVERITIES = new Set<SeverityType>(['success', 'info', 'warning', 'error']);

const Toast: FC<ToastProps> = ({ visible, message, onClose, severity = 'success', ...rest }) => {
  const sev: SeverityType = ALERT_SEVERITIES.has(severity as SeverityType) ? (severity as SeverityType) : 'success';

  const duration = sev === 'success' ? 3000 : 5000;

  return (
    <Snackbar open={visible} autoHideDuration={duration}>
      <Alert onClose={onClose} sx={{ width: '100%' }} severity={sev} {...rest}>
        {message}
      </Alert>
    </Snackbar>
  );
};

export const toast = (opts: Props) => {
  const { message, severity } = opts;

  return hotToast.custom(({ visible, id }) => (
    <Toast
      visible={visible}
      message={message}
      severity={severity}
      onClose={() => {
        hotToast.dismiss(id);
      }}
    />
  ));
};

export const CustomToast: FC<ToastProps> = ({ message, severity = 'success', visible, onClose }) => {
  const sev = severity;
  /** Hex / theme paths that always resolve to a string — avoid `info` alone (undefined color → MUI decomposeColor crash). */
  const clr: Record<string, string> = {
    success: '#09b517',
    info: '#5a95f0',
    warning: '#f0ad4e',
    error: '#db4240',
  };
  const bg = clr[sev] ?? clr.error;

  return (
    <Fade in={visible}>
      <Box
        sx={{
          width: '100%',
          color: '#fff',
          fontSize: 14,
          backgroundColor: bg,
          backgroundImage: 'linear-gradient(rgba(255,255,255,.25) 0,rgba(255,255,255,0) 100%)',
          p: 1,
          borderRadius: 2,
          display: visible ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'space-between',
          my: 1,
        }}
      >
        {message}
        <IconButton size="small" onClick={onClose} sx={{ ml: 1, height: 20, width: 20 }}>
          <CloseIcon sx={{ color: '#fff', width: 16 }} />
        </IconButton>
      </Box>
    </Fade>
  );
};
